import {createStore} from '@ngneat/elf';
import {deleteEntities, selectManyByPredicate, updateEntities, withEntities} from "@ngneat/elf-entities";
import {Injectable} from "@angular/core";
import {erstatteAlleEntiteterMedForelder, FeilhåndteringArgumenter, forelderIdPredikat, leggTilForelderId, Leveringsvalg, logMottatt, oppdaterEntitetMedForelder, opprettEllerOppdatertHendelse, registrerEntitetMedForelder, serverFraLagerOgRequestPakketIMetaRegistrerAbonnenterPåEntitetIder, serverFraRequest, serverFraRequestPakketIMeta, slettAlleEntiteterMedForelder, slettAlleEntiteterMedForeldre, slettHendelse, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {AgendaelementId, BeslutningId, BeslutningRessursClient, FavorittType, ModelloppdateringType, MuterendeOppgaveSpesifikasjon, MøteId, OppdateringmodellOppgave, OppdaterStatusSpesifikasjon, OppgaveId, OppgaveModell, OppgaveRessursClient, Oppgavestatus, RestResponse, SakRessursClient} from "../ressurser/generertNg2HttpKlient";
import {DatalagerAbonnenter} from "felles-ng2/tjenester/datalager/DatalagerAbonnenter";
import {firstValueFrom, Observable, Subject} from "rxjs";
import {ModellOpprettetEllerOppdatertHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellOpprettetEllerOppdatertHendelseHåndteringBygger";
import {ModellSlettetHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellSlettetHendelseHåndteringBygger";
import {sorterLevertSamling} from "felles/ng2/rxjsHjelpere";
import {erBeslutningsoppgave, erSaksoppgave, sorterOppgave} from "felles-ng2/modell/oppgaveModellHjelpere";
import {tap} from "rxjs/operators";
import {DataOgMeta} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {FavorittDatatjeneste} from "felles-ng2/tjenester/datalager/FavorittDatatjeneste";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {skrivZonedDateTimeSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import {KommentarDatatjeneste} from "felles-ng2/tjenester/datalager/KommentarDatatjeneste";
import {i18nArgumenterMedAnsvarligBruker} from "felles-ng2/modell/modelloppdateringModellHjelpere";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {BeslutningDatatjeneste} from "felles-ng2/tjenester/datalager/BeslutningDatatjeneste";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {MøteMedAggregaterDatatjeneste} from "felles-ng2/tjenester/datalager/MøteMedAggregaterDatatjeneste";
import {Temporal} from "temporal-polyfill";
import {Modelloppdateringer} from "felles-ng2/tjenester/Modelloppdateringer";
import ZonedDateTime = Temporal.ZonedDateTime;

const debug = initDebugger('OppgaveDatatjeneste');


/** Datalager for {@link OppgaveModell}.
 * MERK håndterer per nå ikke frittstående oppgaver! */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class OppgaveDatatjeneste {

    /** Lokalt datalager for {@link OppgaveModell} entiteter.
     * Bruker {@link OppgaveModell#forelderRef} som forelder. */
      //public for testbarhet
    readonly oppgaveLager = createStore({name: 'oppgave'}, withEntities<OppgaveModell, 'id'>());

    /** Verdi som brukes som foreldernøkkel dersom oppgave ikke har noen forelder (altså er en selvstendig oppgave, ikke tilknyttet sak eller beslutning) */
    readonly forelderVerdiNårIngenForelder = 'ingenForelder';

    /** Holder rede på åpne subscriptions basert på oppgave.id. Benyttes ifbm modelloppdateringer. */
    readonly oppgaveAbonnenter = new DatalagerAbonnenter<OppgaveId>('oppgave'); //public for testbarhet

    /** Holder rede på åpne subscriptions basert på sakId (oppgave.forelderRef.id når oppgave.forelderRef.type = sak). Benyttes ifbm modelloppdateringer. */
    readonly sakAbonnenter = new DatalagerAbonnenter<AgendaelementId>('oppgave.sak'); //public for testbarhet

    /** Holder rede på åpne subscriptions basert på sakId (oppgave.forelderRef.id når oppgave.forelderRef.type = beslutning). Benyttes ifbm modelloppdateringer. */
    readonly beslutningAbonnenter = new DatalagerAbonnenter<AgendaelementId>('oppgave.beslutning'); //public for testbarhet

    private readonly vedNyOppgaveSubject = new Subject<OppgaveModell>();

    // datatjenestene under må settes etter opprettelse av instans pga sirkularitet
    private sakDatatjeneste!: SakDatatjeneste;

    constructor(private oppgaveRessurs: OppgaveRessursClient,
                private sakRessurs: SakRessursClient,
                private beslutningRessurs: BeslutningRessursClient,
                private kommentarDatatjeneste: KommentarDatatjeneste,
                private beslutningDatatjeneste: BeslutningDatatjeneste,
                private møteMedAggregaterDatatjeneste: MøteMedAggregaterDatatjeneste,
                private favorittDatatjeneste: FavorittDatatjeneste,
                private modelloppdateringer: Modelloppdateringer,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser,
                private klokke: Klokke) {

        this.propagerEndringerTilAndreDatatjenester();
        this.lyttTilModelloppdateringer();
    }

    /** For intern bruk. Datatjenestene under må settes etter opprettelse av instans pga sirkularitet */
    setDatatjenester(sakDatatjeneste: SakDatatjeneste) {
        this.sakDatatjeneste = sakDatatjeneste;
    }

    /** Observerbar som leverer verdier når noen av 'opprett' metodene blir kalt */
    get vedNyOppgave(): Observable<OppgaveModell> {
        return this.vedNyOppgaveSubject;
    }

    /** Returnerer oppgaver tilknyttet angitt sakId */
    finnTilknyttetSakFraLokallager(sakId: AgendaelementId): Observable<OppgaveModell[]> {
        debug('finnTilknyttetSakFraLokallager', sakId, this.sakAbonnenter.antallAbonnenter(sakId))
        return this.sakAbonnenter.observer(sakId,
          this.oppgaveLager.pipe(
            selectManyByPredicate(forelderIdPredikat(sakId)),
            sorterLevertSamling(sorterOppgave),
          ));
    }

    /** Returnerer oppgaver tilknyttet angitt beslutningId */
    finnTilknyttetBeslutningFraLokallager(beslutningId: BeslutningId): Observable<OppgaveModell[]> {
        debug('finnTilknyttetBeslutningFraLokallager', beslutningId, this.beslutningAbonnenter.antallAbonnenter(beslutningId))
        return this.beslutningAbonnenter.observer(beslutningId,
          this.oppgaveLager.pipe(
            selectManyByPredicate(forelderIdPredikat(beslutningId)),
            sorterLevertSamling(sorterOppgave),
            logMottatt(debug, 'finnTilknyttetBeslutningFraLokallager')
          ));
    }

    finnIkkeUtførteOppgaverMedForfallFør(forfallsdato: ZonedDateTime, leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<OppgaveModell[]>> {
        return this.finnOppgaver(() => this.oppgaveRessurs.finnIkkeUtførteOppgaverMedForfallFør({forfallsdato: skrivZonedDateTimeSerialisert(forfallsdato)}), leveringsvalg, feilhåndteringsstrategi);
    }

    finnIkkeUtfoerteOppgaverUtenFrist(leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<OppgaveModell[]>> {
        return this.finnOppgaver(() => this.oppgaveRessurs.finnIkkeUtfoerteOppgaverUtenFrist(), leveringsvalg, feilhåndteringsstrategi);
    }

    /** Erstatter alle oppgaver knytte til angitt sakId i lokallager */
    erstattOppgaverTilknyttetSakILokallager(oppgaver: OppgaveModell[], sakId: AgendaelementId) {
        erstatteAlleEntiteterMedForelder(this.oppgaveLager, oppgaver, sakId);
    }

    /** Sletter alle oppgaver knytte til angitt sakId i lokallager */
    slettOppgaverTilknyttetSakILokallager(sakId: AgendaelementId) {
        slettAlleEntiteterMedForelder(this.oppgaveLager, sakId);
    }

    /** Sletter alle oppgaver knyttet til angitte sakIder i lokallager */
    slettOppgaverTilknyttetSakerILokallager(sakIder: AgendaelementId[]) {
        slettAlleEntiteterMedForeldre(this.oppgaveLager, sakIder);
    }

    /** Sletter alle oppgaver knytte til angitt beslutningId i lokallager */
    slettOppgaverTilknyttetBeslutningILokallager(beslutningId: AgendaelementId) {
        slettAlleEntiteterMedForelder(this.oppgaveLager, beslutningId);
    }

    /** Sletter alle oppgaver knyttet til angitte beslutningIder i lokallager */
    slettOppgaverTilknyttetBeslutningerILokallager(beslutningIder: AgendaelementId[]) {
        slettAlleEntiteterMedForeldre(this.oppgaveLager, beslutningIder);
    }

    /** Oppretter ny selvstendig oppgave. Hvilken gruppe oppgaven registreres under bestemmes av spesifikasjon.ansvarlig */
    opprettUnderGruppe(spesifikasjon: MuterendeOppgaveSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<OppgaveModell>> {
        return this.opprett(() => this.oppgaveRessurs.opprett(spesifikasjon), feilhåndteringsstrategi)
    }

    opprettUnderSak(sakId: AgendaelementId, spesifikasjon: MuterendeOppgaveSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<OppgaveModell>> {
        return this.opprett(() => this.sakRessurs.opprettOppgave(sakId, spesifikasjon), feilhåndteringsstrategi)
    }

    opprettUnderBeslutning(beslutningId: BeslutningId, spesifikasjon: MuterendeOppgaveSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<OppgaveModell>> {
        return this.opprett(() => this.beslutningRessurs.opprettOppgave(beslutningId, spesifikasjon), feilhåndteringsstrategi)
    }

    oppdater(id: OppgaveId, spesifikasjon: MuterendeOppgaveSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<OppgaveModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.oppgaveRessurs.oppdater(id, spesifikasjon).råRespons()
              .pipe(tap(respons => oppdaterEntitetMedForelder(this.oppgaveLager, respons.body!, this.forelderIdFraOppgave(respons.body!)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere oppgave')), 'oppdater')
        }));
    }

    /** Registrerer angitt oppgave som bruker favoritt dersom ikke allere markert som favoritt,
     * eller fjernes angitt oppgave som brukers favoritt  */
    async endreFavorittmarkering(oppgave: OppgaveModell): Promise<void> {
        const favoritterOppdatert = await this.favorittDatatjeneste.endreFavorittmarkering(oppgave, FavorittType.oppgave);
        this.oppgaveLager.update(updateEntities(oppgave.id, {favoritter: favoritterOppdatert}));
    }

    /** Endrer fra status til utført dersom status ikke er utført,
     * endrer til ikke_påbegynt ellers. */
    async inverterUtførtStatus(oppgave: OppgaveModell, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta) {
        const nyStatus = oppgave.utfoert ? Oppgavestatus.ikke_påbegynt : Oppgavestatus.utført;
        const spesifikasjon: OppdaterStatusSpesifikasjon = {
            status: nyStatus,
            utfoertDato: nyStatus == Oppgavestatus.utført ? skrivZonedDateTimeSerialisert(this.klokke.nåSomZonedDateTime()) : null
        }
        return this.oppdaterStatus(oppgave.id, spesifikasjon);
    }

    async oppdaterStatus(id: OppgaveId, spesifikasjon: OppdaterStatusSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<OppgaveModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.oppgaveRessurs.settStatus(id, spesifikasjon).råRespons()
              .pipe(tap(respons => oppdaterEntitetMedForelder(this.oppgaveLager, respons.body!, this.forelderIdFraOppgave(respons.body!)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere oppgave')), 'oppdater')
        }));
    }

    async slett(id: OppgaveId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest({
            ressurskall: () => this.oppgaveRessurs.slett(id).råRespons()
              .pipe(tap(respons => this.oppgaveLager.update(deleteEntities(id)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke slette oppgave')), 'slett')
        }));
    }

    //hjelpemetoder

    private finnOppgaver(ressurskall: () => RestResponse<OppgaveModell[]>, leveringsvalg: Leveringsvalg, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta) {
        return serverFraLagerOgRequestPakketIMetaRegistrerAbonnenterPåEntitetIder({
            ressurskall,
            lager: this.oppgaveLager,
            abonnentRegister: this.oppgaveAbonnenter,
            forelderId: oppgave => this.forelderIdFraOppgave(oppgave),
            leveringsvalg,
            feilhåndteringArgumenter: this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke finne oppgaver', 'finnIkkeUtfoerteOppgaverUtenFrist')
        });
    }

    private opprett(ressursKall: () => RestResponse<OppgaveModell>, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<OppgaveModell>> {
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => ressursKall().råRespons()
              .pipe(tap(respons => {
                  registrerEntitetMedForelder(this.oppgaveLager, respons.body!, this.forelderIdFraOppgave(respons.body!));
                  this.vedNyOppgaveSubject.next(respons.body!);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere oppgave')), 'opprettUnderSak')
        }));
    }

    /** Returnerer iden til angitt oppgaves forelder.
     * Merk at oppgave kan være tilknyttet sak, beslutning eller ingen ting.
     * Siden {@link OppdateringmodellOppgave} kun inneholder forelder-id (og ikke type)
     * ignorerer vi her forelderRef.type */
    private forelderIdFraOppgave(oppgave: OppgaveModell): AgendaelementId | BeslutningId | string {
        return oppgave.forelderRef?.id ?? this.forelderVerdiNårIngenForelder;
    }

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(
      feilhåndteringsstrategi: STRATEGI,
      brukerrettetFeilmelding: string,
      metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

    /** Ved endringer i oppgave må lager som håndterer data inkludert i oppgave oppdateres */
    private propagerEndringerTilAndreDatatjenester() {
        opprettEllerOppdatertHendelse(this.oppgaveLager).subscribe(({hendelse, entiteter}) => {
            debug(`propagerer oppgaveLager ${hendelse.type} til relatert datatjenester (kom/møteAgr) for oppgavene ${hendelse.ids}`, hendelse, entiteter);
            entiteter
              .forEach(async oppgave => {
                  this.kommentarDatatjeneste.erstattILokallager(oppgave.kommentarer, oppgave.id);
                  await this.propagerEndringerTilMøteMedAggregaterDatatjeneste(oppgave);
              });
        });

        slettHendelse(this.oppgaveLager).subscribe(hendelse => {
            debug(`propagerer oppgaveLager ${hendelse.type} til relatert datatjenester (kom) for oppgavene ${hendelse.ids}`);
            this.kommentarDatatjeneste.slettKommentarerTilknyttetForelderILokallager(hendelse.ids);
            //kan ikke propagere endring til møteMedAggregaterDatatjeneste - mangler info om møteId
        });
    }

    /** propager endring i angitt oppgave til møteMedAggregaterDatatjeneste for å holde tellere i MøteMedAggregaterModell i synk */
    private async propagerEndringerTilMøteMedAggregaterDatatjeneste(oppgave: OppgaveModell) {
        const møteId = this.møteIdKnyttetTilOppgave(oppgave);
        if (erIkkeNil(møteId))
            this.møteMedAggregaterDatatjeneste.oppdaterAntallOppgaverIILokallager(møteId, await this.oppgaverKnyttetTilMøte(møteId));

    }

    private møteIdKnyttetTilOppgave(oppgave: OppgaveModell): MøteId | null {
        if (erSaksoppgave(oppgave))
            return this.sakDatatjeneste.hentFraLokallager(oppgave.forelderRef.id)?.moete.id ?? null;
        if (erBeslutningsoppgave(oppgave)) {
            const sakId = this.beslutningDatatjeneste.hentFraLokallager(oppgave.forelderRef.id)?.sakId ?? null;
            if (erIkkeNil(sakId))
                return this.sakDatatjeneste.hentFraLokallager(sakId)?.moete.id ?? null;
        }
        return null;
    }

    /** Returnerer alle oppgaver knyttet til samme møte som angitt sak via rollen som saksoppgave eller beslutningsoppgave, data hentes fra lokallager */
    private async oppgaverKnyttetTilMøte(møteId: MøteId): Promise<OppgaveModell[]> {
        const alleSaker = (await firstValueFrom(this.sakDatatjeneste.finnTilknyttetMøteFraLokallager(møteId))).map(sak => sak.id);
        const alleBeslutninger = (await firstValueFrom(this.beslutningDatatjeneste.finnTilknyttetSakerFraLokallager(alleSaker))).map(beslutning => beslutning.id);
        return firstValueFrom(this.oppgaveLager.pipe(selectManyByPredicate(oppgave =>
          (erSaksoppgave(oppgave) && alleSaker.includes(oppgave.forelderRef.id)) ||
          (erBeslutningsoppgave(oppgave) && alleBeslutninger.includes(oppgave.forelderRef.id)))));
    }

    /**
     * Lytt til relevante modelloppdateringer via {@link Modelloppdateringer} (push-baserte oppdateringer).
     * Merk at vi aldri avregistrerer subscription vi oppretter her da data-tjenesten aldri termineres.
     */
    private lyttTilModelloppdateringer() {
        this.lyttTilNyOppgave();
        this.lyttTilOppgaveOppdatert();
        this.lyttTilOppgaveSlettet();
    }

    private lyttTilNyOppgave() {
        this.opprettetEllerOppdatertOppgaveHendelse(ModelloppdateringType.NY_OPPGAVE)
          .medAbonnentoppslag(this.oppgaveAbonnenter, hendelseModell => hendelseModell.oppgaveId)
          .medAbonnentoppslagOpt(this.sakAbonnenter, hendelseModell => hendelseModell.oppgaveForelderId)
          .medAbonnentoppslagOpt(this.beslutningAbonnenter, hendelseModell => hendelseModell.oppgaveForelderId)
          .medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Ny Oppgave registrert (av {bruker})'), i18nArgumenterMedAnsvarligBruker(oppdatering)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilOppgaveOppdatert() {
        this.opprettetEllerOppdatertOppgaveHendelse(ModelloppdateringType.OPPGAVE_OPPDATERT)
          .medAbonnentoppslag(this.oppgaveAbonnenter, hendelseModell => hendelseModell.oppgaveId)
          .medAbonnentoppslagOpt(this.sakAbonnenter, hendelseModell => hendelseModell.oppgaveForelderId)
          .medAbonnentoppslagOpt(this.beslutningAbonnenter, hendelseModell => hendelseModell.oppgaveForelderId)
          .medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Oppgave har blitt oppdatert (av {bruker})'), i18nArgumenterMedAnsvarligBruker(oppdatering)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilOppgaveSlettet() {
        this.slettetHendelse(ModelloppdateringType.OPPGAVE_SLETTET)
          .medFørLagerOppdatert(oppdatering => this.visMelding(i18n('Oppgave har blitt slettet (av {bruker})'), i18nArgumenterMedAnsvarligBruker(oppdatering)))
          .oppdaterLagerVedHendelse();
    }

    private slettetHendelse(hendelseType: ModelloppdateringType) {
        return new ModellSlettetHendelseHåndteringBygger<OppdateringmodellOppgave, OppgaveModell>(this.oppgaveLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell.oppgaveId);
    }

    private opprettetEllerOppdatertOppgaveHendelse(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<OppdateringmodellOppgave, OppgaveModell>(this.oppgaveLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell.oppgaveId)
          .medEntitetLaster(hendelseModell => this.oppgaveRessurs.hent(hendelseModell.oppgaveId).råRespons())
          .medForberedLageroppdatering(oppgave => leggTilForelderId(oppgave, this.forelderIdFraOppgave(oppgave)));
    }

    private visMelding(melding: string, argumenter = {}) {
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(melding, argumenter));
    }

}
